"use client";

import {Children, isValidElement, JSX, ReactNode, useState} from "react";

import SmartImage from "@/shared/ui/smart-image";

import {type KeenSliderInstance, useKeenSlider} from "keen-slider/react";

import {type SpaceProps, useSectionSpace} from "@/sections/use-section-space";

import {ButtonBase} from "@/shared/ui/buttons/button-base";
import {ButtonWithImage} from "@/shared/ui/buttons/button-with-image";
import {Typography} from "@/shared/ui/data-display/typography";
import {ContainerBase} from "@/shared/ui/layout/container-base";
import {assetPath} from "@/shared/utils/asset-path";

import "./styles/info-with-slider.scss";
import "keen-slider/keen-slider.min.css";

export type TextBlock = {
    id: number;
    title: string;
    description: string;
};

export type ButtonProps = {
    text: string;
    url: string;
    target?: "_self" | "_blank";
};

export type InfoWithSliderProps = {
    icon_url?: string;
    title: string;
    text_blocks?: Array<TextBlock>;
    button?: ButtonProps;
    children: ReactNode;
    space?: SpaceProps;
    anchorId?: string;
};

export function InfoWithSlider({icon_url, title, text_blocks, button, children, space, anchorId}: InfoWithSliderProps): JSX.Element {
    const sectionVars = useSectionSpace(space);

    const childrenArray = Children.toArray(children).filter(isValidElement);
    const childrenCount = childrenArray.length;
    const shouldUseSlider = childrenCount > 1;
    const isTwoSlides = childrenCount === 2;
    const isThreeSlides = childrenCount === 3;

    const [sliderInstance, setSliderInstance] = useState<KeenSliderInstance | null>(null);

    const [sliderRef] = useKeenSlider(
        shouldUseSlider
            ? {
                  slides: {
                      perView: isTwoSlides ? 1.5 : isThreeSlides ? 2.5 : 3.5,
                      spacing: 15,
                  },
                  breakpoints: {
                      "(max-width: 1440px)": {
                          slides: {
                              spacing: 15,
                              perView: isTwoSlides ? 1.5 : 2.5,
                          },
                      },
                      "(max-width: 1224px)": {
                          slides: {
                              spacing: 15,
                              perView: isTwoSlides ? 1.5 : 2,
                          },
                      },
                      "(max-width: 991px)": {
                          slides: {
                              spacing: 15,
                              perView: 3,
                          },
                      },
                      "(max-width: 852px)": {
                          slides: {
                              spacing: 15,
                              perView: 2.6,
                          },
                      },
                      "(max-width: 768px)": {
                          slides: {
                              spacing: 15,
                              perView: 1.6,
                          },
                      },
                  },
                  drag: true,
                  loop: childrenCount > 2,
                  mode: "snap",
                  rtl: false,
              }
            : undefined,
        [
            (slider) => {
                setSliderInstance(slider);
            },
        ],
    );

    return (
        <section className={`info-with-slider ${shouldUseSlider ? "info-with-slider__slider-view" : ""}`} style={sectionVars} id={anchorId}>
            <ContainerBase>
                <div className="info-with-slider__container">
                    <div className="info-with-slider__content">
                        {(icon_url || title || text_blocks) && (
                            <div className="info-with-slider__info">
                                {icon_url && (
                                    <div className="info-with-slider__icon">
                                        <SmartImage src={icon_url} alt="" width={24} aria-hidden="true" height={24} />
                                    </div>
                                )}
                                {title && (
                                    <Typography as="h2" size={{desktop: 32, mobile: 20}} className="info-with-slider__title">
                                        {title}
                                    </Typography>
                                )}
                                {text_blocks && text_blocks.length > 0 && (
                                    <div className="info-with-slider__text-blocks">
                                        {text_blocks.map((block, index) => (
                                            <div key={`${block.id}-${index}`} className="info-with-slider__text-block">
                                                {block.title && (
                                                    <Typography as="h3" size={{desktop: 16, mobile: 14}} className="info-with-slider__text-title">
                                                        {block.title}
                                                    </Typography>
                                                )}
                                                <Typography
                                                    as="p"
                                                    size={14}
                                                    weight={400}
                                                    color="paragraph"
                                                    className="info-with-slider__text"
                                                    innerHTML={block.description}
                                                ></Typography>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {button && (
                                    <div className="info-with-slider__button">
                                        <ButtonBase
                                            as="link"
                                            href={button.url}
                                            variant="secondary-outline"
                                            size="sm"
                                            width="auto"
                                            target={button.target || "_self"}
                                        >
                                            {button.text}
                                            <SmartImage
                                                src={assetPath("/images/icons/navigation/arrow-right-v2.svg")}
                                                alt=""
                                                width={16}
                                                height={16}
                                                aria-hidden="true"
                                            />
                                        </ButtonBase>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className={`info-with-slider__slider-wrap ${shouldUseSlider ? "info-with-slider__slider-wrap--fullwidth" : ""}`}>
                            {shouldUseSlider ? (
                                <>
                                    <div ref={sliderRef} className="keen-slider info-with-slider__slider">
                                        {childrenArray.map((child, index) => {
                                            const key = isValidElement(child) && child.key ? child.key : index;
                                            return (
                                                <div key={key} className="keen-slider__slide info-with-slider__slide">
                                                    {child}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <div className="info-with-slider__navigation-wrap">
                                        <div className="info-with-slider__navigation">
                                            <ButtonWithImage
                                                variant="default"
                                                onClick={() => sliderInstance?.prev()}
                                                aria-label="Previous slide"
                                                className="info-with-slider__nav-button info-with-slider__nav-button--prev"
                                            >
                                                <SmartImage
                                                    src={assetPath("/images/icons/navigation/arrow-left.svg")}
                                                    alt=""
                                                    width={16}
                                                    height={16}
                                                    aria-hidden="true"
                                                />
                                            </ButtonWithImage>
                                            <ButtonWithImage
                                                variant="default"
                                                onClick={() => sliderInstance?.next()}
                                                aria-label="Next slide"
                                                className="info-with-slider__nav-button info-with-slider__nav-button--next"
                                            >
                                                <SmartImage
                                                    src={assetPath("/images/icons/navigation/arrow-right.svg")}
                                                    alt=""
                                                    width={16}
                                                    height={16}
                                                    aria-hidden="true"
                                                />
                                            </ButtonWithImage>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className="info-with-slider__content-items">{children}</div>
                            )}
                        </div>
                    </div>
                </div>
            </ContainerBase>
        </section>
    );
}
